/**
 * @fileoverview One sortable column card on the report canvas — mirrors the BandCard
 *             chrome from DocumentCanvasBuilder adapted to report columns.
 * @module ui-admin/components/ReportCanvasBuilder/CanvasColumnCard
 *
 * Responsibilities: - Render a column card (drag handle, label + format badge,
 *                     parameter-bound badge, enabled checkbox, overflow menu,
 *                     help affordance, optional sample-values preview).
 * Dependencies:     - @dnd-kit/sortable, lucide-react, @firesquirrel/local-db
 *                     (ReportColumn type), @firesquirrel/core-domain
 *                     (ReportParameter for the bound badge).
 * Architecture:     - Presentation-only, controlled — all mutations are callbacks
 *                     the host applies to its columns array.
 * Notes:            - Columns are keyed by `field` (unique per report); sortable
 *                     order maps to the report table's left-to-right column order.
 */

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, MoreVertical, ArrowUp, ArrowDown, Copy, Trash2, HelpCircle, Link2,
} from 'lucide-react';
import type { ReportColumn, ReportParameter } from '@firesquirrel/local-db';
import { REPORT_TOUR_TARGET } from './reportTourSteps';

/**
 * @contract CanvasColumnCardProps
 * Purpose: Props for one sortable column card.
 * Fields:
 * - column: the ReportColumn the card renders (keyed by field).
 * - index / total: position for Move up/down guards.
 * - selected: whether this column is the canvas selection.
 * - parameter: the bound ReportParameter when column.parameterId resolves
 *   (drives the violet "🔗 Parameter" badge + filter affordance).
 * - sampleValues: optional preview values for the column (host-derived).
 * - tourCard / tourMenu: whether this card hosts the tour anchors.
 * - Callbacks: onSelect / onMove / onDuplicate / onDelete / onToggleVisible / onToggleHelp.
 * - helpOpen: whether the per-column help popover is open.
 */
/** Stable identity for a column card — columnId when present, else field. */
export function columnKey(column: ReportColumn): string {
  return column.columnId ?? column.field;
}

export interface CanvasColumnCardProps {
  column: ReportColumn;
  index: number;
  total: number;
  selected: boolean;
  parameter?: ReportParameter;
  sampleValues?: string[];
  tourCard?: boolean;
  tourMenu?: boolean;
  helpOpen?: boolean;
  onSelect: () => void;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggleVisible: () => void;
  onToggleHelp: () => void;
}

/** Format badge label for a column card. */
function formatLabel(column: ReportColumn): string {
  const parts: string[] = [];
  if (column.format) parts.push(column.format.toUpperCase());
  if (column.sortOrder !== undefined) parts.push(`SORT ${column.sortOrder === 1 ? 'ASC' : 'DESC'}`);
  return parts.join(' · ');
}

/** Overflow (⋮) menu for one column card. */
function ColumnOverflowMenu({
  index, total, onMove, onDuplicate, onDelete, tourMenu,
}: {
  index: number; total: number; tourMenu?: boolean;
  onMove: (dir: -1 | 1) => void; onDuplicate: () => void; onDelete: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Column actions"
        aria-expanded={open}
        data-tour={tourMenu ? REPORT_TOUR_TARGET.columnMenu : undefined}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div role="menu" onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-full z-10 mt-1 w-44 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          <button type="button" role="menuitem" aria-label="Move up" disabled={index === 0}
            onClick={() => { onMove(-1); setOpen(false); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:text-gray-300">
            <ArrowUp className="h-3.5 w-3.5" /> Move up
          </button>
          <button type="button" role="menuitem" aria-label="Move down" disabled={index === total - 1}
            onClick={() => { onMove(1); setOpen(false); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:text-gray-300">
            <ArrowDown className="h-3.5 w-3.5" /> Move down
          </button>
          <button type="button" role="menuitem" aria-label="Duplicate column"
            onClick={() => { onDuplicate(); setOpen(false); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50">
            <Copy className="h-3.5 w-3.5" /> Duplicate column
          </button>
          <button type="button" role="menuitem" aria-label="Remove column"
            onClick={() => { onDelete(); setOpen(false); }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5" /> Remove column
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * @component CanvasColumnCard
 * Purpose:     Draggable column card on the paged report canvas.
 * Props:       CanvasColumnCardProps.
 * Behavior:    Click selects the column (opens the portaled properties panel);
 *              the enabled checkbox toggles `visible` without selecting; drag
 *              reorders via the parent's sortable context.
 * Data Source: none (controlled).
 * Constraints: presentation-only.
 */
export function CanvasColumnCard({
  column, index, total, selected, parameter, sampleValues,
  tourCard, tourMenu, helpOpen,
  onSelect, onMove, onDuplicate, onDelete, onToggleVisible, onToggleHelp,
}: CanvasColumnCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: columnKey(column) });
  const [showSample, setShowSample] = React.useState(false);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hidden = column.visible === false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`column-card-${columnKey(column)}`}
      data-tour={tourCard ? REPORT_TOUR_TARGET.columnCard : undefined}
      onClick={onSelect}
      className={`mb-2 cursor-pointer rounded-lg border bg-white p-3 transition-shadow ${
        selected ? 'border-orange-500 ring-2 ring-orange-300' : 'border-gray-300 hover:border-gray-400'
      } ${hidden ? 'bg-gray-50' : ''}`}
    >
      <div className="flex items-center gap-2">
        <button type="button" aria-label="Drag to reorder" {...attributes} {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="cursor-grab border-none bg-transparent text-gray-400 hover:text-gray-600">
          <GripVertical className="h-4 w-4" />
        </button>
        <span className={`font-semibold ${hidden ? 'text-gray-400' : 'text-gray-800'}`}>
          {column.label}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-gray-400">
          COLUMN{formatLabel(column) ? ` · ${formatLabel(column)}` : ''}
        </span>
        {parameter && (
          <span className="flex items-center gap-1 rounded-sm border border-violet-200 px-1.5 py-px text-[10px] text-violet-700">
            <Link2 className="h-2.5 w-2.5" /> Parameter: {parameter.label}
          </span>
        )}
        <span className="flex-1" />
        <label className="flex items-center gap-1 text-[11px] text-gray-500" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" aria-label="Column enabled" checked={!hidden} onChange={onToggleVisible} />
        </label>
        <ColumnOverflowMenu
          index={index} total={total} tourMenu={tourMenu}
          onMove={onMove} onDuplicate={onDuplicate} onDelete={onDelete}
        />
        <button
          type="button"
          aria-label={`Help for ${column.label}`}
          aria-expanded={!!helpOpen}
          onClick={(e) => { e.stopPropagation(); onToggleHelp(); }}
          className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-1 text-[11px] text-gray-400">
        Field: <span className="font-mono">{column.field}</span>
        {column.width !== undefined && <> · Width: {column.width}px</>}
      </div>

      {(parameter || (sampleValues && sampleValues.length > 0)) && (
        <div className="mt-2 rounded-md border border-dashed border-violet-300 bg-violet-50 p-2 text-xs text-violet-800">
          {parameter && <div>Filtered by runtime parameter &ldquo;{parameter.label}&rdquo;.</div>}
          {sampleValues && sampleValues.length > 0 && (
            <>
              <button type="button" data-testid="sample-values-toggle"
                onClick={(e) => { e.stopPropagation(); setShowSample((v) => !v); }}
                className="mt-0.5 cursor-pointer border-none bg-transparent text-[11px] text-violet-700 underline">
                {showSample ? 'Hide sample values' : 'Preview sample values'}
              </button>
              {showSample && (
                <div data-testid="sample-values" className="mt-1.5 rounded-sm border border-gray-200 bg-white p-1.5 text-gray-700">
                  {sampleValues.join('  ·  ')}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
